/**
 * Centralized Logger Utility
 *
 * Provides consistent logging across the application with environment-aware output.
 * Console statements are allowed here and controlled by ESLint configuration.
 *
 * Features:
 * - Development-only logging (no logs in production)
 * - Structured logging with context support
 * - Error boundary specific logging
 * - Easy to extend with production error monitoring services
 */

import type { ErrorInfo } from 'react';
import { isDevelopment } from './featureFlags';
import { Sentry } from '@/lib/sentry';

type LogLevel = 'error' | 'warn' | 'info' | 'log';

interface LogContext {
  [key: string]: unknown;
}

/**
 * Extended context for invitation-specific logging
 */
export interface InvitationLogContext extends LogContext {
  stage?: 'validation' | 'oauth' | 'redemption' | 'error';
  errorType?: string;
  provider?: 'google' | 'microsoft' | 'email';
  timestamp?: string;
  errorMessage?: string;
  email?: string;
}

/**
 * Internal logging function that respects environment settings
 */
const logWithLevel = (level: LogLevel, message: string, context?: unknown): void => {
  if (!isDevelopment()) {
    return;
  }

  if (context !== undefined && context !== null) {
    console[level](message, context);
  } else {
    console[level](message);
  }
};

/**
 * Centralized logger object
 *
 * All methods only log in development mode.
 * In production, logs are suppressed (can be extended with error monitoring services).
 */
export const logger = {
  /**
   * Log error messages
   */
  error: (message: string, context?: unknown): void => {
    logWithLevel('error', message, context);
    if (!isDevelopment()) {
      const err = isError(context) ? context : new Error(message);
      Sentry.captureException(err, {
        extra: isError(context)
          ? { message }
          : typeof context === 'object' && context !== null
            ? (context as Record<string, unknown>)
            : { context },
      });
    }
  },

  /**
   * Log warning messages
   */
  warn: (message: string, context?: unknown): void => {
    logWithLevel('warn', message, context);
  },

  /**
   * Log informational messages
   */
  info: (message: string, context?: unknown): void => {
    logWithLevel('info', message, context);
  },

  /**
   * Log debug messages
   */
  log: (message: string, context?: unknown): void => {
    logWithLevel('log', message, context);
  },

  /**
   * Specialized logging for Error Boundaries
   * Provides detailed error information with component stack trace
   */
  logErrorBoundary: (error: Error, errorInfo?: ErrorInfo): void => {
    if (!isDevelopment()) {
      return;
    }

    console.group('🚨 Error Boundary');
    console.error('Error:', error.name);
    console.error('Message:', error.message);
    console.error('Stack:', error.stack);

    if (errorInfo?.componentStack) {
      console.error('Component Stack:', errorInfo.componentStack);
    }

    console.groupEnd();
  },

  /**
   * Specialized logging for WebSocket errors
   */
  logWebSocketError: (message: string, error?: unknown): void => {
    if (!isDevelopment()) {
      return;
    }

    console.group('🔌 WebSocket Error');
    console.error(message);

    if (error) {
      console.error('Details:', error);
    }

    console.groupEnd();
  },

  /**
   * Log group start (for organizing related logs)
   */
  group: (label: string): void => {
    if (!isDevelopment()) {
      return;
    }
    console.group(label);
  },

  /**
   * Log group end
   */
  groupEnd: (): void => {
    if (!isDevelopment()) {
      return;
    }
    console.groupEnd();
  },

  /**
   * Specialized logging for invitation flow errors
   * Includes stage and error information
   *
   * @param level - Log level (error, warn, info, log)
   * @param message - Log message
   * @param context - Invitation-specific context
   *
   * @example
   * ```typescript
   * logger.logInvitation('error', 'Validation failed', {
   *   stage: 'validation',
   *   errorType: 'INVITATION_EXPIRED',
   *   errorMessage: 'Invitation has expired',
   *   timestamp: new Date().toISOString()
   * });
   * ```
   */
  logInvitation: (level: LogLevel, message: string, context: InvitationLogContext): void => {
    if (!isDevelopment()) {
      return;
    }

    const enhancedContext = {
      ...context,
      flow: 'invitation-acceptance',
      timestamp: context.timestamp || new Date().toISOString(),
    };

    const prefixedMessage = `[Invitation] ${message}`;
    logWithLevel(level, prefixedMessage, enhancedContext);
  },
};

/**
 * Type guard for Error objects
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Format error for logging
 */
export const formatError = (error: unknown): string => {
  if (isError(error)) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
};
